/**
 * 路由权限配置
 */
export interface RoutePermission {
  /**
   * 权限编码（单个或数组）— legacy 或叶子均可（permissions 集合）
   */
  permission?: string | string[];
  /**
   * 矩阵叶子码（单个或数组）— 优先用 matrixLeaves 校验
   */
  matrix?: string | string[];
  /**
   * 角色编码（单个或数组）
   */
  role?: string | string[];
  /**
   * 是否要求所有权限/角色（默认false）
   */
  requireAll?: boolean;
  /**
   * 无权限时的重定向路径（默认'/dashboard'）
   */
  redirect?: string;
}

/**
 * 扩展路由元信息
 */
declare module 'vue-router' {
  interface RouteMeta {
    /**
     * 是否需要认证（默认true）
     */
    requiresAuth?: boolean;
    /**
     * 权限配置
     */
    permission?: RoutePermission;
    /**
     * 路由标题（用于菜单显示）
     */
    title?: string;
    /**
     * 路由图标（用于菜单显示）
     */
    icon?: string;
    /**
     * 是否在菜单中显示（默认true）
     */
    showInMenu?: boolean;
    /**
     * 菜单排序
     */
    menuOrder?: number;
  }
}

/**
 * 路由权限配置映射
 * key: 路由名称
 * value: 权限配置
 */
export const routePermissionMap: Record<string, RoutePermission> = {
  // 项目相关
  ProjectDashboard: {
    matrix: 'project-management-matrix-1-3',
    redirect: '/dashboard',
  },
  /** 创建项目：与后端 ProjectController#create 一致（SUPER_ADMIN / ADMIN / SE / PM） */
  ProjectCreate: {
    role: ['SUPER_ADMIN', 'ADMIN', 'SE', 'PM'],
    redirect: '/dashboard',
  },
  ProjectEdit: {
    matrix: 'basic-info-matrix-1-2',
    redirect: '/dashboard',
  },
  ProjectDelete: {
    permission: 'project:delete',
    redirect: '/dashboard',
  },
  ProjectApprove: {
    permission: 'project:approve:stage',
    redirect: '/dashboard',
  },
  ProjectReport: {
    permission: 'project:report:progress',
    redirect: '/dashboard',
  },

  /** 报告审批：仅项目经理可访问 */
  ReportApproval: {
    role: 'PM',
    redirect: '/dashboard',
  },
  
  // 需求相关
  Requirements: {
    matrix: 'requirements-matrix-1-7',
    redirect: '/dashboard',
  },
  RequirementCreate: {
    matrix: 'requirements-matrix-1-1',
    redirect: '/requirements',
  },
  RequirementManage: {
    matrix: 'requirements-matrix-1-9',
    redirect: '/requirements',
  },
  
  // 设计相关
  Designs: {
    permission: 'design:read',
    redirect: '/dashboard',
  },
  DesignCreate: {
    permission: 'design:create',
    redirect: '/designs',
  },
  DesignReview: {
    permission: 'design:review',
    redirect: '/designs',
  },
  
  // 资源相关（暂时对通用部件库不做权限限制）
  // Resources: {
  //   permission: 'resource:read',
  //   redirect: '/dashboard',
  // },
  /** 新建部件：与后端 POST /api/components 一致（SE/ADMIN/USER + 超级管理员） */
  ResourceCreate: {
    role: ['SE', 'ADMIN', 'USER', 'SUPER_ADMIN'],
    redirect: '/resources',
  },
  ResourceAllocate: {
    permission: 'resource:allocate',
    redirect: '/resources',
  },
  
  // 财务相关
  Finance: {
    permission: 'finance:read',
    role: 'SE',
    redirect: '/dashboard',
  },
  
  // 用户管理
  UserManagement: {
    permission: 'user:read',
    redirect: '/dashboard',
  },
  UserRoleAssignment: {
    permission: 'user:role:manage',
    role: 'SE',
    requireAll: true,
    redirect: '/dashboard',
  },
  
  // 组织架构
  OrgChart: {
    permission: 'user:read',
    redirect: '/dashboard',
  },
  DepartmentManagement: {
    permission: 'user:manage',
    redirect: '/dashboard',
  },
  
  // 同步管理
  SyncManagement: {
    permission: 'system:config',
    role: 'SE',
    requireAll: true,
    redirect: '/dashboard',
  },
  
  // 职位映射
  PositionMapping: {
    permission: 'user:role:assign',
    role: 'SE',
    requireAll: true,
    redirect: '/dashboard',
  },

  // 组织架构管理（超管 + 子管理员）
  OrgAdmin: {
    role: ['SUPER_ADMIN', 'ADMIN'],
    redirect: '/dashboard',
  },
  // 兼容旧路径
  DepartmentRoleMapping: {
    role: ['SUPER_ADMIN', 'ADMIN'],
    redirect: '/dashboard',
  },
};

/**
 * 检查路由权限
 * @param routeName 路由名称
 * @param hasPermission 权限检查函数
 * @param hasRole 角色检查函数
 * @param hasMatrix 矩阵叶子检查函数（可选）
 */
export function checkRoutePermission(
  routeName: string,
  hasPermission: (codes: string | string[], requireAll?: boolean) => boolean,
  hasRole: (codes: string | string[], requireAll?: boolean) => boolean,
  hasMatrix?: (codes: string | string[], requireAll?: boolean) => boolean
): boolean {
  const permissionConfig = routePermissionMap[routeName];
  
  if (!permissionConfig) {
    // 没有配置权限，默认允许访问
    return true;
  }

  let hasPerm = true;
  let hasRoleCheck = true;
  let hasMatrixCheck = true;

  if (permissionConfig.matrix && hasMatrix) {
    const leaves = Array.isArray(permissionConfig.matrix)
      ? permissionConfig.matrix
      : [permissionConfig.matrix];
    hasMatrixCheck = hasMatrix(leaves, permissionConfig.requireAll);
  } else if (permissionConfig.matrix && !hasMatrix) {
    // 无矩阵检查器时回退到 permissions 集合
    const leaves = Array.isArray(permissionConfig.matrix)
      ? permissionConfig.matrix
      : [permissionConfig.matrix];
    hasMatrixCheck = hasPermission(leaves, permissionConfig.requireAll);
  }

  // 检查权限
  if (permissionConfig.permission) {
    const permissions = Array.isArray(permissionConfig.permission)
      ? permissionConfig.permission
      : [permissionConfig.permission];
    hasPerm = hasPermission(permissions, permissionConfig.requireAll);
  }

  // 检查角色
  if (permissionConfig.role) {
    const roles = Array.isArray(permissionConfig.role)
      ? permissionConfig.role
      : [permissionConfig.role];
    hasRoleCheck = hasRole(roles, permissionConfig.requireAll);
  }

  const checks: boolean[] = [];
  if (permissionConfig.matrix) checks.push(hasMatrixCheck);
  if (permissionConfig.permission) checks.push(hasPerm);
  if (permissionConfig.role) checks.push(hasRoleCheck);
  if (checks.length === 0) {
    return true;
  }
  return checks.every(Boolean);
}

/**
 * 获取路由权限配置
 */
export function getRoutePermission(routeName: string): RoutePermission | undefined {
  return routePermissionMap[routeName];
}

/**
 * 根据权限过滤路由（用于动态生成菜单）
 */
export function filterRoutesByPermission(
  routes: any[],
  hasPermission: (codes: string | string[], requireAll?: boolean) => boolean,
  hasRole: (codes: string | string[], requireAll?: boolean) => boolean
): any[] {
  return routes.filter(route => {
    // 检查当前路由
    if (route.name && routePermissionMap[route.name as string]) {
      return checkRoutePermission(
        route.name as string,
        hasPermission,
        hasRole
      );
    }

    // 递归检查子路由
    if (route.children && route.children.length > 0) {
      route.children = filterRoutesByPermission(route.children, hasPermission, hasRole);
      return route.children.length > 0;
    }

    // 没有配置权限，默认允许
    return true;
  });
}
